import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Key, EnvelopeSimple, Copy, Trash, CheckCircle } from "@phosphor-icons/react";

export default function AccessTokens() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [email, setEmail] = useState("");
  const [credits, setCredits] = useState("1");
  const [saving, setSaving] = useState(false);
  const [grants, setGrants] = useState([]);

  const loadGrants = () => {
    if (!isAdmin) return;
    api.get("/settings/access-tokens/grants").then(r => setGrants(r.data || [])).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadGrants(); }, [isAdmin]);

  // Hard guard: staff should never see this page even if they type the URL.
  // Must render AFTER all hooks are declared (React rules of hooks).
  if (user && !isAdmin) {
    return (
      <div className="p-10 max-w-xl">
        <div className="border border-[#F1C21B] bg-[#F1C21B]/10 p-6 rounded-sm">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#8A6A00] mb-1">Restricted</div>
          <h1 className="text-xl font-semibold text-[#111827]">Admin only</h1>
          <p className="mt-2 text-sm text-[#6B7280]">
            Access Tokens are managed by administrators. Please contact your admin if you need more credits.
          </p>
        </div>
      </div>
    );
  }

  const grant = async () => {
    const n = parseInt(credits, 10);
    if (!email.trim()) return toast.error("Enter a customer email");
    if (!n || n < 1) return toast.error("Enter a positive credit amount");
    setSaving(true);
    try {
      const { data } = await api.post("/settings/access-tokens/grant-by-email",
        { email: email.trim(), credits: n });
      toast.success(`Granted ${data.granted} credit(s) to ${data.email} · balance ${data.balance}`);
      setEmail(""); setCredits("1"); loadGrants();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const copyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success(`Copied ${email}`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const removeGrant = async (g) => {
    if (!window.confirm(
      `Reverse this grant?\n\n${g.credits} credit(s) will be removed from ${g.user_email}.`
    )) return;
    try {
      await api.delete(`/settings/access-tokens/grants/${g.id}`);
      toast.success("Grant reversed");
      loadGrants();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="p-8 max-w-[1200px]" data-testid="access-tokens-page">
      {/* Header */}
      <header className="mb-6 flex items-start gap-4">
        <div className="h-11 w-11 rounded-md bg-[#1E6BC6] flex items-center justify-center shrink-0">
          <Key size={22} weight="duotone" className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Access Tokens</h1>
          <p className="mt-1 text-sm text-[#6B7280] max-w-3xl">
            Grant secretary-file credits to customers who purchase. Each credit lets them create one secretary file (company).
          </p>
        </div>
      </header>

      {/* Grant card */}
      <section className="bg-white rounded-lg shadow-sm border border-[#E5E7EB] p-6" data-testid="grant-credits-card">
        <div className="font-mono uppercase tracking-[0.15em] text-xs text-[#1E6BC6] font-semibold">
          Grant Credits By Email
        </div>
        <p className="mt-2 text-sm text-[#6B7280]">
          Add credits straight to a registered customer's account (no code to share). The customer must already have signed up.
        </p>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-3 items-end">
          <div>
            <Label className="text-sm text-[#374151]">Customer email</Label>
            <Input type="email" placeholder="customer@example.com" value={email}
                   onChange={e => setEmail(e.target.value)}
                   className="mt-1 rounded-md h-11" data-testid="grant-email-input" />
          </div>
          <div>
            <Label className="text-sm text-[#374151]">Credits (secretary files)</Label>
            <Input type="number" min="1" value={credits}
                   onChange={e => setCredits(e.target.value)}
                   className="mt-1 rounded-md h-11 text-right" data-testid="grant-credits-input" />
          </div>
          <Button className="h-11 rounded-md bg-[#10B981] hover:bg-[#0E9F6E] px-5 text-white"
                  onClick={grant} disabled={saving || !isAdmin} data-testid="grant-btn">
            <EnvelopeSimple size={16} weight="bold" className="mr-2" />
            {saving ? "Granting…" : "Grant"}
          </Button>
        </div>
        {!isAdmin && (
          <p className="mt-3 text-xs text-[#DA1E28]">Only admins can grant credits.</p>
        )}
      </section>

      {/* Grants table */}
      {isAdmin && (
        <section className="mt-6 bg-white rounded-lg shadow-sm border border-[#E5E7EB] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <Th>Code</Th>
                <Th className="text-right">Credits</Th>
                <Th>Note</Th>
                <Th>Status</Th>
                <Th>Redeemed by</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {grants.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-[#9CA3AF]">
                    No grants yet. Use the form above to grant credits by email.
                  </td>
                </tr>
              )}
              {grants.map(g => (
                <tr key={g.id} className="border-b last:border-b-0 border-[#E5E7EB] hover:bg-[#FAFAFA]"
                    data-testid={`grant-row-${g.id}`}>
                  <td className="px-6 py-4 font-mono text-[13px] text-[#111827] tracking-wider">DIRECT-GRANT</td>
                  <td className="px-6 py-4 font-mono text-right">{g.credits}</td>
                  <td className="px-6 py-4 text-[#4B5563]">Direct grant to {g.user_email}</td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#DEFBE6] text-[#0E6027]">
                      <CheckCircle size={13} weight="fill" /> Redeemed
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[#4B5563]">{g.user_email}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8"
                              onClick={() => copyEmail(g.user_email)}
                              title="Copy email" data-testid={`grant-copy-${g.id}`}>
                        <Copy size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-[#DA1E28]"
                              onClick={() => removeGrant(g)}
                              title="Reverse grant" data-testid={`grant-del-${g.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) =>
  <th className={`px-6 py-3 font-medium text-xs text-[#6B7280] ${className}`}>{children}</th>;
